import { AxiosResponse } from 'axios';
import {
  School,
  District,
  MarketplaceSchoolsResponse,
  JobDiscoverySearchParamsInterface,
} from 'types/connectTypes.js';
import { BaseNimbleAPI } from '../../../api/baseAPI.js';

interface GetSchoolsByNcesIdRequest {
  ncesId: string;
}

interface GetDistrictSlugByNcesIdRequest {
  ncesId: string;
}

interface DistrictSlugResponse {
  slug: string;
}

class MarketplaceSchoolsAPI extends BaseNimbleAPI {
  get config(): { baseURL: string } {
    return { baseURL: '/api/marketplace/' };
  }

  async getSchoolFilterOptions({
    stateCode,
    districtNcesIds,
  }: {
    stateCode: string;
    districtNcesIds?: string[];
  }): Promise<School[]> {
    const { data }: AxiosResponse = await this.get('list/schools/', {
      params: { state_abbreviation: stateCode, district_nces_ids: districtNcesIds },
    });

    return data;
  }

  async getDistrictFilterOptions({ stateCode }: { stateCode: string }): Promise<District[]> {
    const { data }: AxiosResponse = await this.get('list/districts/', {
      params: { state_abbreviation: stateCode },
    });

    return data;
  }

  async getSchools(
    searchParams: JobDiscoverySearchParamsInterface
  ): Promise<MarketplaceSchoolsResponse> {
    const { data }: AxiosResponse = await this.get('schools/', {
      params: searchParams,
    });

    return data;
  }

  async getSchoolByNcesId({
    ncesId,
    signal,
  }: GetSchoolsByNcesIdRequest & { signal?: AbortSignal }): Promise<School | null> {
    if (!ncesId) throw new Error('NCES ID is required');

    const { data, status }: AxiosResponse = await this.get(`schools/${ncesId}/`, {
      signal,
    });
    if (status !== 200) throw new Error('Failed to get schools by NCES ID');

    return data;
  }

  async getDistrictSlugByNcesId({
    ncesId,
    signal,
  }: GetDistrictSlugByNcesIdRequest & { signal?: AbortSignal }): Promise<string> {
    if (!ncesId) throw new Error('NCES ID is required');

    const { data, status }: AxiosResponse<DistrictSlugResponse> = await this.get(
      `districts/${ncesId}/slug/`,
      {
        signal,
      }
    );
    if (status !== 200) throw new Error('Failed to get district slug by NCES ID');

    return data.slug;
  }
}

export default new MarketplaceSchoolsAPI();
